import { useState } from "react";
import { X, Plus, Check } from "lucide-react";
import { TEAM_COLORS } from "@/lib/colors";
import { toast } from "sonner";

const inputCls = "h-11 bg-[#0A0A0A] border border-white/15 px-3 text-base sm:text-sm focus:outline-none focus:border-[#CCFF00]";

const AddField = ({ testId, placeholder, type = "text", onAdd }) => {
  const [v, setV] = useState("");
  const submit = () => { if (v.trim() && onAdd(v.trim())) setV(""); };
  return (
    <div className="flex gap-2 mt-3">
      <input data-testid={`${testId}-input`} type={type} value={v} placeholder={placeholder} onChange={(e) => setV(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && submit()} className={`${inputCls} flex-1 min-w-0`} />
      <button data-testid={`${testId}-add`} onClick={submit} className="tap h-11 w-11 bg-white text-black flex items-center justify-center hover:bg-[#CCFF00]">
        <Plus size={18} />
      </button>
    </div>
  );
};

const Chip = ({ children, onRemove, testId, dim }) => (
  <span data-testid={testId} className={`inline-flex items-center gap-1 h-10 pl-3 pr-1 border ${dim ? "border-white/10 text-zinc-400" : "border-white/25"} bg-[#131313]`}>
    <span className="font-display text-lg font-bold">{children}</span>
    <button onClick={onRemove} data-testid={`${testId}-remove`} className="tap w-7 h-7 flex items-center justify-center text-zinc-500 hover:text-[#FF3B30]"><X size={14} /></button>
  </span>
);

const PlayerChip = ({ n, dim, testId, onRemove, onRename }) => {
  const [edit, setEdit] = useState(null);
  const commit = () => { if (edit !== null && String(edit) !== String(n)) onRename(n, edit); setEdit(null); };
  if (edit !== null)
    return (
      <input data-testid={`${testId}-edit-input`} autoFocus type="number" value={edit} onChange={(e) => setEdit(e.target.value)}
        onBlur={commit} onKeyDown={(e) => { if (e.key === "Enter") commit(); if (e.key === "Escape") setEdit(null); }}
        className="w-16 h-10 bg-[#0A0A0A] border border-[#CCFF00] px-2 font-display text-lg font-bold focus:outline-none" />
    );
  return (
    <span data-testid={testId} className={`inline-flex items-center gap-1 h-10 pl-1 pr-1 border ${dim ? "border-white/10 text-zinc-400" : "border-white/25"} bg-[#131313]`}>
      <button data-testid={`${testId}-edit`} title="Editar número" onClick={() => setEdit(String(n))}
        className="px-2 font-display text-lg font-bold hover:text-[#CCFF00]">{n}</button>
      <button onClick={onRemove} data-testid={`${testId}-remove`} className="tap w-7 h-7 flex items-center justify-center text-zinc-500 hover:text-[#FF3B30]"><X size={14} /></button>
    </span>
  );
};

export const TeamEditor = ({ side, team, onChange, onRename }) => {
  const validNumber = (val, except) => {
    const n = parseInt(val, 10);
    if (!n || n < 1 || n > 99) return toast.error("Número inválido (1-99)"), null;
    if (n !== except && team.players.some((p) => p.number === n)) return toast.error(`Número ${n} já existe`), null;
    return n;
  };
  const addPlayer = (starter) => (val) => {
    const n = validNumber(val);
    if (!n) return false;
    onChange({ ...team, players: [...team.players, { number: n, starter }].sort((a, b) => a.number - b.number) });
    return true;
  };
  const renamePlayer = (old, val) => {
    const n = validNumber(val, old);
    if (!n) return false;
    onChange({ ...team, players: team.players.map((p) => (p.number === old ? { ...p, number: n } : p)).sort((a, b) => a.number - b.number) });
    onRename?.(String(old), String(n));
    toast.success(`Nº ${old} alterado para Nº ${n}`);
    return true;
  };
  const removePlayer = (n) => onChange({ ...team, players: team.players.filter((p) => p.number !== n) });
  const group = (starter) => team.players.filter((p) => p.starter === starter);

  return (
    <div data-testid={`team-editor-${side}`} className="border border-white/15 bg-[#131313] p-4 sm:p-8">
      <p className="label-caps mb-2">{side === "home" ? "Equipa da casa" : "Equipa visitante"}</p>
      <input data-testid={`team-name-${side}`} value={team.name} onChange={(e) => onChange({ ...team, name: e.target.value })}
        className="w-full bg-transparent border-b border-white/20 font-display text-3xl sm:text-4xl font-bold uppercase pb-2 focus:outline-none focus:border-[#CCFF00]" />
      <p className="label-caps mt-6 mb-3">Cor da equipa</p>
      <div data-testid={`team-colors-${side}`} className="flex flex-wrap gap-2">
        {TEAM_COLORS.map((c) => (
          <button key={c.key} data-testid={`team-color-${side}-${c.key}`} title={c.label} aria-label={c.label}
            onClick={() => onChange({ ...team, color: c.key })}
            className={`tap w-11 h-11 rounded-full border border-white/30 flex items-center justify-center ${team.color === c.key ? "ring-4 ring-[#CCFF00] ring-offset-2 ring-offset-[#131313]" : "hover:scale-110"}`}
            style={{ background: c.bg, color: c.text }}>
            {team.color === c.key && <Check size={18} />}
          </button>
        ))}
      </div>
      {[true, false].map((starter) => (
        <div key={String(starter)} className="mt-8">
          <p className="label-caps mb-3">{starter ? "Titulares" : "Suplentes"} · {group(starter).length}</p>
          <div className="flex flex-wrap gap-2">
            {group(starter).map((p) => (
              <PlayerChip key={p.number} n={p.number} dim={!starter} testId={`${side}-player-${p.number}`}
                onRemove={() => removePlayer(p.number)} onRename={renamePlayer} />
            ))}
          </div>
          <p className="text-xs text-zinc-500 mt-2">Toque num número para o alterar.</p>
          <AddField testId={`${side}-${starter ? "starter" : "sub"}`} type="number" placeholder="Nº do jogador" onAdd={addPlayer(starter)} />
        </div>
      ))}
      <div className="mt-8">
        <p className="label-caps mb-3">Elementos oficiais · {team.officials.length}</p>
        <div className="flex flex-wrap gap-2">
          {team.officials.map((o, i) => (
            <Chip key={i} testId={`${side}-official-${i}`} onRemove={() => onChange({ ...team, officials: team.officials.filter((_, j) => j !== i) })}>{o}</Chip>
          ))}
        </div>
        <AddField testId={`${side}-official`} placeholder="Ex: Treinador, Médico..." onAdd={(v) => (onChange({ ...team, officials: [...team.officials, v] }), true)} />
      </div>
    </div>
  );
};
